import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreateWarehouseDemandRequestBatchDto,
  CreateWarehouseDemandRequestLineDto,
  WarehouseQuantityMeasure,
} from 'woofwoof-api';
import * as Yup from 'yup';
import { translateKey } from 'src/locales/translate';
import { parseQuantityInput } from '../../utils/quantity';
import { WarehouseProductLike } from '../../utils/saleForm';
import { StockDisplayProduct } from '../../utils/stockDisplay';

export const DEMAND_NAME_MAX = 120;
export const DEMAND_NOTE_MAX = 500;
/** Backend `WAREHOUSE_DEMAND_BATCH_MAX_LINES`: a longer batch is refused with a 400. */
export const DEMAND_MAX_LINES = 50;

/** What the Warehouse holds of a Product, for its line's "Na stanie". */
export interface DemandProductStock extends StockDisplayProduct {
  onHandMilli: number;
}

export interface DemandProductOption {
  id: string;
  name: string;
  /** Unknown for a Product the launcher knows only by id and name. */
  stock?: DemandProductStock;
}

export function toDemandProductOption(
  product: WarehouseProductLike,
): DemandProductOption {
  const { id, name, onHandMilli, unit, unitCustom, unitsPerPackage } = product;
  return {
    id,
    name,
    ...(onHandMilli !== undefined &&
      unit && {
        stock: {
          onHandMilli,
          unit,
          unitCustom,
          unitsPerPackage: unitsPerPackage ?? 1,
        },
      }),
  };
}

/** A Warehouse Product (`product`) or, without one, a product outside the Warehouse by name. */
export interface DemandLineValues {
  product: DemandProductOption | null;
  requestedName: string;
  quantity: string;
  measure: WarehouseQuantityMeasure;
}

export interface DemandRequestFormValues {
  lines: DemandLineValues[];
  /** One note for the whole submission. */
  note: string;
}

export function catalogueDemandLine(
  product: DemandProductOption,
): DemandLineValues {
  return {
    product: {
      id: product.id,
      name: product.name,
      ...(product.stock && { stock: product.stock }),
    },
    requestedName: '',
    quantity: '',
    measure: WarehouseQuantityMeasure.Package,
  };
}

export function freeTextDemandLine(name: string): DemandLineValues {
  return {
    product: null,
    requestedName: name.trim().slice(0, DEMAND_NAME_MAX),
    quantity: '',
    measure: WarehouseQuantityMeasure.Package,
  };
}

/**
 * A Warehouse Product wins; otherwise a name (#690: a registry medicine the
 * Warehouse does not hold) starts a line for another product.
 */
export function buildDemandRequestDefaults(
  product?: DemandProductOption | null,
  requestedName?: string | null,
): DemandRequestFormValues {
  const nameLine = freeTextDemandLine(requestedName ?? '');
  let lines: DemandLineValues[] = [];
  if (product) {
    lines = [catalogueDemandLine(product)];
  } else if (nameLine.requestedName) {
    lines = [nameLine];
  }
  return { lines, note: '' };
}

const v = (key: string): string =>
  translateKey(`warehouse.demand.validation.${key}`);

const demandLineSchema: Yup.ObjectSchema<DemandLineValues> = Yup.object({
  product: Yup.object({
    id: Yup.string().required(),
    name: Yup.string().required(),
    stock: Yup.mixed<DemandProductStock>().optional(),
  })
    .nullable()
    .defined(),
  // The backend checks "exactly one of product and name" only with a
  // generic 400, so a line without a Product must carry a name here.
  requestedName: Yup.string()
    .defined()
    .trim()
    .max(DEMAND_NAME_MAX, v('nameTooLong'))
    .when('product', {
      is: (product: DemandProductOption | null) => !product,
      then: (schema) => schema.required(v('nameRequired')),
    }),
  quantity: Yup.string()
    .defined()
    .test(
      'quantity',
      v('quantityInvalid'),
      (value) => !value?.trim() || parseQuantityInput(value) !== null,
    ),
  measure: Yup.mixed<WarehouseQuantityMeasure>()
    .oneOf(Object.values(WarehouseQuantityMeasure))
    .required(),
});

export const demandRequestFormSchema: Yup.ObjectSchema<DemandRequestFormValues> =
  Yup.object({
    // The form never offers a submit without a line or a line past the cap;
    // these rules are the backstop.
    lines: Yup.array(demandLineSchema)
      .defined()
      .min(1, translateKey('warehouse.demand.form.noLines'))
      .max(DEMAND_MAX_LINES, translateKey('warehouse.demand.form.maxLines')),
    note: Yup.string().defined().max(DEMAND_NOTE_MAX, v('noteTooLong')),
  });

// Stable resolver instance, as medicationFormSchema.ts does.
export const demandRequestFormResolver = yupResolver(demandRequestFormSchema);

/**
 * Exactly one of `productId` and `requestedName`; a quantity always goes with
 * its measure. A free-text line has no `unitsPerPackage`, so its quantity
 * always goes out in packages (contract note 5).
 */
function toLineDto(
  line: DemandLineValues,
): CreateWarehouseDemandRequestLineDto {
  const quantity = line.quantity.trim()
    ? parseQuantityInput(line.quantity)
    : null;
  return {
    ...(line.product
      ? { productId: line.product.id }
      : { requestedName: line.requestedName.trim() }),
    ...(quantity !== null
      ? {
          quantity,
          measure: line.product
            ? line.measure
            : WarehouseQuantityMeasure.Package,
        }
      : {}),
  };
}

export function buildDemandBatchPayload(
  values: DemandRequestFormValues,
): Omit<CreateWarehouseDemandRequestBatchDto, 'operationKey'> {
  const note = values.note.trim();
  return {
    lines: values.lines.map(toLineDto),
    ...(note ? { note } : {}),
  };
}
